import React, { useState, useEffect, useRef } from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import { useNavigate } from 'react-router-dom';
import { Drawer, Card, Switch, Select, InputNumber, Space } from 'antd';
import ReceiptPreview from './ReceiptPreview';
import { printReceiptHTML } from './ReceiptPrint';
import TransactionHistory from './TransactionHistory';
import BentoOrderNotificationEnhanced from './BentoOrderNotificationEnhanced';
import BentoTimeAdjuster from './BentoTimeAdjuster';
import PaymentCalculatorEnhanced from './PaymentCalculatorEnhanced';
import BentoScanModal from './BentoScanModal';
import CouponInputModal from './CouponInputModal';
import PrizeExchangeModal from './PrizeExchangeModal';
import { useReceipt } from '../../hooks/useReceipt';
import DailyClosingReportHTML from './DailyClosingReportHTML';
import { getOperationMode } from '../../hooks/useOperationMode';

interface MenuItem {
  id: string;
  category: string;
  name: string;
  price: number;
  stock: number;
  active: boolean;
  is_takeout_available?: boolean;
  service_type?: 'eatin_only' | 'takeout_only' | 'both';
}

interface OrderItem extends MenuItem {
  quantity: number;
  bentoOrderId?: number;
}

// システム状態の保存用インターフェース
interface SystemState {
  orderItems: OrderItem[];
  orderType: 'eatin' | 'takeout';
  paymentType: 'cash' | 'paypay';
  selectedCategory: string;
  savedAt: string;
}

const POSRegisterFullScreen: React.FC = () => {
  const navigate = useNavigate();
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [orderType, setOrderType] = useState<'eatin' | 'takeout'>('eatin');
  const [paymentType, setPaymentType] = useState<'cash' | 'paypay'>('cash');
  const [receivedAmount, setReceivedAmount] = useState<string>('');
  const [taxSettings, setTaxSettings] = useState<{
    standard_rate: number;
    reduced_rate: number;
    tax_mode: 'internal' | 'external' | 'exempt';
    is_tax_exempt: boolean;
  }>({ standard_rate: 10, reduced_rate: 8, tax_mode: 'internal', is_tax_exempt: false });
  const [pointsPerYen, setPointsPerYen] = useState<number>(1000); // デフォルト: 1000円で1ポイント
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [lastTransaction, setLastTransaction] = useState<any>(null);
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [transactionHistoryFilter, setTransactionHistoryFilter] = useState<'all' | 'takeout'>('all');
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showClosingConfirm, setShowClosingConfirm] = useState(false);
  const [showClosingReport, setShowClosingReport] = useState(false);
  const [closingSummary, setClosingSummary] = useState<any>(null);
  const [preparedClaimId, setPreparedClaimId] = useState<string | null>(null);
  const [preparedPoints, setPreparedPoints] = useState<number>(0);
  const [showBentoTimeModal, setShowBentoTimeModal] = useState(false);
  const [showBentoScanModal, setShowBentoScanModal] = useState(false);
  const [busyTimeDelay, setBusyTimeDelay] = useState<number>(0);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [showCouponConfirmation, setShowCouponConfirmation] = useState(false);
  const [validatedCoupon, setValidatedCoupon] = useState<any>(null);
  const [bentoUnreadCount, setBentoUnreadCount] = useState(0);
  const [showPrizeExchangeModal, setShowPrizeExchangeModal] = useState(false);
  const { printReceipt, generatePDF } = useReceipt();

  // 値引き機能用の状態変数
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountInfo, setDiscountInfo] = useState<{
    type: 'percent' | 'amount';
    value: number;
  } | null>(null);
  const [discountType, setDiscountType] = useState<'percent' | 'amount'>('amount');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [discountError, setDiscountError] = useState<string>('');
  const scanBufferRef = useRef<string>("");
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchMenus();
    fetchTaxSettings();
    fetchPointSettings();
    restoreSystemState();

    // 出張モード時は注文タイプを自動的にテイクアウトに設定
    const currentMode = getOperationMode();
    if (currentMode === 'event') {
      setOrderType('takeout');
      console.log('✅ 出張モード: 注文タイプを「テイクアウト」に自動設定');
    }

    // 忙しい時間の遅延を取得
    const loadBusyTimeDelay = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/bento-orders/busy-time`);
        if (!response.ok) {
          throw new Error(`Failed to fetch busy time: ${response.status}`);
        }

        const data = await response.json();
        const apiDelay = data?.data?.additional_minutes ?? 0;
        setBusyTimeDelay(apiDelay);
        localStorage.setItem('busyTimeDelay', apiDelay.toString());
      } catch (error) {
        const savedDelay = parseInt(localStorage.getItem('busyTimeDelay') || '0');
        setBusyTimeDelay(savedDelay);
      }
    };
    loadBusyTimeDelay();

    // イベントリスナーを設定
    const handleBusyTimeChange = (event: CustomEvent) => {
      setBusyTimeDelay(event.detail.delay);
    };
    window.addEventListener('busyTimeChanged', handleBusyTimeChange as EventListener);

    // 出張モード切り替えイベントリスナー
    const handleOperationModeChange = (event: CustomEvent) => {
      const newMode = event.detail.mode;
      if (newMode === 'event') {
        setOrderType('takeout');
        console.log('✅ 出張モードに切替: 注文タイプを「テイクアウト」に自動設定');
      }
      // 通常モードに戻った場合は、ユーザーが選択できるようにする
    };
    window.addEventListener('operationModeChanged', handleOperationModeChange as EventListener);

    // 未読数を定期的に更新
    const unreadInterval = setInterval(() => {
      const count = (window as any).bentoUnreadCount || 0;
      setBentoUnreadCount(count);
    }, 500);

    return () => {
      window.removeEventListener('busyTimeChanged', handleBusyTimeChange as EventListener);
      window.removeEventListener('operationModeChanged', handleOperationModeChange as EventListener);
      clearInterval(unreadInterval);
    };
  }, []);

  // 弁当バーコードスキャン用キーボードリスナー
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // モーダルが開いている時はスキャンを無効化
      if (showCouponModal || showPrizeExchangeModal || showPaymentDialog || showBentoScanModal) {
        return;
      }

      if (e.key === 'Enter') {
        if (scanBufferRef.current.startsWith('BENTO:')) {
          handleBentoScan(scanBufferRef.current);
        }
        scanBufferRef.current = '';
        return;
      }

      if (e.key.length === 1) {
        const newBuffer = scanBufferRef.current + e.key;
        scanBufferRef.current += e.key;
        if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = setTimeout(() => {
          scanBufferRef.current = '';
        }, 500);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
    };
  }, [showCouponModal, showPrizeExchangeModal, showPaymentDialog, showBentoScanModal]);

  // 弁当バーコードスキャンハンドラー
  const handleBentoScan = async (barcodeData: string) => {
    try {
      const normalizedData = barcodeData.trim();
      const rawId = normalizedData.startsWith('BENTO:') ? normalizedData.replace('BENTO:', '') : normalizedData;
      const bentoOrderId = parseInt(rawId, 10);
      if (isNaN(bentoOrderId)) {
        const message = '無効なバーコードです';
        setErrorMessage(message);
        return { success: false, message };
      }

      const response = await apiClient.get(`/bento-orders/${bentoOrderId}`);
      if (!response.data.success) {
        const message = '注文が見つかりません';
        setErrorMessage(message);
        return { success: false, message };
      }

      const order = response.data.data;
      let items: any[] = [];
      try {
        items = Array.isArray(order.items) ? order.items : JSON.parse(order.items || '[]');
      } catch (parseError) {
        items = [];
      }
      if (items.length === 0) {
        const message = '注文内容が空です';
        setErrorMessage(message);
        return { success: false, message };
      }

      // 弁当予約会計は常にテイクアウト税率で処理する
      setOrderType('takeout');

      items.forEach((item: any) => {
        const cartItem = {
          id: item.id || `BENTO_${order.id}_${item.name}`,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          category: '弁当',
          bentoOrderId: order.id,
          tax_type: 'reduced' as 'reduced'
        };

        setOrderItems((prevCart) => {
          const existingIndex = prevCart.findIndex(c => c.id === cartItem.id);
          if (existingIndex >= 0) {
            const newCart = [...prevCart];
            newCart[existingIndex].quantity += cartItem.quantity;
            return newCart;
          }
          return [...prevCart, cartItem];
        });
      });

      setErrorMessage('');
      return { success: true };
    } catch (error: any) {
      console.error('弁当スキャンエラー:', error);
      const message = error.response?.data?.error || '弁当注文の読み込みに失敗しました';
      setErrorMessage(message);
      return { success: false, message };
    }
  };



  // システム状態を保存
  const saveSystemState = () => {
    const state: SystemState = {
      orderItems,
      orderType,
      paymentType,
      selectedCategory,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem('waton_pos_state', JSON.stringify(state));
    console.log('システム状態を保存しました:', state);
  };

  // システム状態を復元
  const restoreSystemState = () => {
    const savedState = localStorage.getItem('waton_pos_state');
    if (savedState) {
      try {
        const state: SystemState = JSON.parse(savedState);
        const savedDate = new Date(state.savedAt);
        const now = new Date();
        const diffHours = (now.getTime() - savedDate.getTime()) / (1000 * 60 * 60);
        
        // 24時間以内の状態のみ復元
        if (diffHours < 24) {
          setOrderItems(state.orderItems);
          setOrderType(state.orderType);
          setPaymentType(state.paymentType);
          setSelectedCategory(state.selectedCategory);
          console.log('システム状態を復元しました:', state);
        } else {
          // 古い状態は削除
          localStorage.removeItem('waton_pos_state');
        }
      } catch (error) {
        console.error('状態の復元に失敗:', error);
        localStorage.removeItem('waton_pos_state');
      }
    }
  };

  // システム状態をクリア
  const clearSystemState = () => {
    localStorage.removeItem('waton_pos_state');
    setOrderItems([]);
    setOrderType('eatin');
    setPaymentType('cash');
    setSelectedCategory('all');
  };

  // claim_id事前生成関数
  const prepareClaimId = async (overrideTotal?: number) => {
    try {
      // 環境変数対応のAPIクライアントを使用
      const response = await fetch(`${getApiUrl()}/points/generate-claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transactionId: Date.now(), // 一時的なトランザクションID
          total: overrideTotal || getTotal(),
          points: Math.floor((overrideTotal || getTotal()) / pointsPerYen) // ポイント計算（動的設定値）
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.claimId) {
          setPreparedClaimId(result.claimId);
          setPreparedPoints(result.points || Math.floor((overrideTotal || getTotal()) / pointsPerYen)); // ポイントも保存
          console.log('Claim ID prepared in advance:', result.claimId);
          console.log('Points prepared:', result.points || Math.floor(getTotal() / pointsPerYen));
          console.log('[DEBUG] pointsPerYen=', pointsPerYen, '(overrideTotal || getTotal())=', (overrideTotal || getTotal()), '計算結果=', Math.floor((overrideTotal || getTotal()) / pointsPerYen));
        } else {
          console.error('No claimId in response:', result);
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to generate claim ID:', response.status, errorText);
      }
    } catch (error) {
      console.error('Failed to prepare claim ID:', error);
      // エラーが発生しても処理は続行
    }
  };

  const fetchTaxSettings = async () => {
    try {
      const response = await apiClient.get('/tax-settings');
      if (response.data) {
        setTaxSettings(response.data);
        console.log('Tax settings loaded:', response.data);
      }
    } catch (error) {
      console.error('Failed to fetch tax settings:', error);
      // Use default values if fetch fails
    }
  };

  const fetchPointSettings = async () => {
    try {
      const response = await apiClient.get('/point-settings/settings');
      if (response.data && response.data.points_per_yen) {
        setPointsPerYen(response.data.points_per_yen);
        console.log('Point settings loaded: points_per_yen=', response.data.points_per_yen);
      }
    } catch (error) {
      console.error('Failed to fetch point settings:', error);
      // Use default 1000 if fetch fails
    }
  };

  const fetchMenus = async () => {
    setLoading(true);
    try {
      // Use apiClient with environment variables support
      const apiUrl = getApiUrl();
      console.log('Environment VITE_API_URL:', import.meta.env.VITE_API_URL);
      console.log('Environment VITE_BACKEND_URL:', import.meta.env.VITE_BACKEND_URL);
      console.log('API Base URL:', apiUrl);
      console.log('Fetching from:', `${apiUrl}/menus`);
      console.log('Current location:', window.location.href);

      const response = await apiClient.get('/menus', {
        timeout: 10000, // 10 second timeout
      });
      console.log('Fetched menus:', response.data);
      if (response.data && Array.isArray(response.data)) {
        setMenus(response.data);
      } else {
        console.error('Invalid menu data format:', response.data);
        setMenus([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch menus:', error);
      console.error('Error details:', error.message);
      
      let errorMsg = 'メニューの読み込みに失敗しました。';
      if (error.code === 'ECONNABORTED') {
        errorMsg += ' タイムアウトしました。';
      } else if (error.response) {
        console.error('Response error:', error.response.status, error.response.data);
        errorMsg += ` サーバーエラー: ${error.response.status}`;
      } else if (error.request) {
        console.error('No response received:', error.request);
        errorMsg += ` サーバーに接続できません。APIホスト: ${window.location.hostname}:3001`;
      } else {
        errorMsg += ` ${error.message}`;
      }
      
      setErrorMessage(errorMsg);
      setMenus([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...Array.from(new Set(menus.map(m => m.category))).filter(c => (c ?? '').trim() !== '弁当')];

  // 出張モード判定
  const operationMode = getOperationMode();
  const isEventMode = operationMode === 'event';

  // メニューフィルタリング: カテゴリー + 出張モード
  const filteredMenus = menus.filter(m => {
    if ((m.category ?? '').trim() === '弁当') {
      return false;
    }

    // カテゴリーフィルター
    if (selectedCategory !== 'all' && m.category !== selectedCategory) {
      return false;
    }

    // 出張モード時: テイクアウト可能な商品のみ表示
    if (isEventMode) {
      const serviceType = m.service_type || (m.is_takeout_available === false ? 'eatin_only' : 'both');
      // eatin_only（店内専用）は表示しない
      if (serviceType === 'eatin_only') {
        return false;
      }
    }

    return true;
  });

  const addToOrder = (menu: MenuItem) => {
    // service_typeまたはis_takeout_availableに基づいて制限
    const serviceType = menu.service_type || (menu.is_takeout_available === false ? 'eatin_only' : 'both');

    if (orderType === 'takeout' && serviceType === 'eatin_only') {
      alert(`「${menu.name}」は店内専用メニューです。`);
      return;
    }

    if (orderType === 'eatin' && serviceType === 'takeout_only') {
      alert(`「${menu.name}」はテイクアウト専用メニューです。`);
      return;
    }

    const existing = orderItems.find(item => item.id === menu.id);
    if (existing) {
      setOrderItems(orderItems.map(item =>
        item.id === menu.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setOrderItems([...orderItems, { ...menu, quantity: 1 }]);
    }
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity === 0) {
      setOrderItems(orderItems.filter(item => item.id !== id));
    } else {
      setOrderItems(orderItems.map(item =>
        item.id === id ? { ...item, quantity } : item
      ));
    }
  };

  // クーポン割引額を計算
  const getCouponDiscount = () => {
    if (!appliedCoupon) return 0;

    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

    if (appliedCoupon.discount_type === 'fixed') {
      // 固定金額割引
      return appliedCoupon.discount_value;
    } else if (appliedCoupon.discount_type === 'percentage') {
      // パーセント割引
      return Math.floor(subtotal * appliedCoupon.discount_value / 100);
    }
    return 0;
  };

  // 手動値引き額を計算
  const getManualDiscount = () => {
    if (!discountInfo) return 0;

    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

    if (discountInfo.type === 'amount') {
      // 円値引き: 小計を超えないようにする
      return Math.min(discountInfo.value, subtotal);
    } else if (discountInfo.type === 'percent') {
      // ％値引き: 小計の指定%を計算（小数点以下切り捨て）
      return Math.floor(subtotal * discountInfo.value / 100);
    }

    return 0;
  };

  const hasBentoToken = JSON.stringify(orderItems).includes('BENTO_');
  const effectiveOrderType = hasBentoToken ? 'takeout' : orderType;

  // 税計算を柔軟に対応
  const getTotal = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const totalDiscount = getCouponDiscount() + getManualDiscount(); // クーポンと手動値引きの合計

    // 免税事業者モード
    if (taxSettings.is_tax_exempt || taxSettings.tax_mode === 'exempt') {
      return Math.max(0, subtotal - totalDiscount);
    }

    // 内税・外税に応じた処理
    if (taxSettings.tax_mode === 'internal') {
      // 内税：価格はすでに税込
      return Math.max(0, subtotal - totalDiscount);
    } else {
      // 外税：税を加算
      const taxRate = effectiveOrderType === 'takeout' ? taxSettings.reduced_rate : taxSettings.standard_rate;
      const tax = Math.floor(subtotal * taxRate / 100);
      return Math.max(0, subtotal + tax - totalDiscount);
    }
  };

  const getTax = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

    // 免税事業者モード
    if (taxSettings.is_tax_exempt || taxSettings.tax_mode === 'exempt') {
      return 0;
    }

    const taxRate = effectiveOrderType === 'takeout' ? taxSettings.reduced_rate : taxSettings.standard_rate;

    if (taxSettings.tax_mode === 'internal') {
      // 内税：税込価格から逆算
      return Math.floor(subtotal - (subtotal / (1 + taxRate / 100)));
    } else {
      // 外税：税を計算
      return Math.floor(subtotal * taxRate / 100);
    }
  };

  const getSubtotal = () => {
    const total = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

    if (taxSettings.tax_mode === 'internal') {
      // 内税：税抜価格を算出
      return total - getTax();
    } else {
      // 外税・免税：価格そのまま
      return total;
    }
  };

  const getChange = () => {
    const received = parseInt(receivedAmount) || 0;
    return received - getTotal();
  };

  // クーポン検証成功時のハンドラー
  const handleCouponValidated = (couponData: any) => {
    setValidatedCoupon(couponData);
    setShowCouponModal(false);
    setShowCouponConfirmation(true);
  };

  // クーポン適用のハンドラー
  const handleApplyCoupon = async () => {
    if (!validatedCoupon) return;

    setLoading(true);
    try {
      // トランザクションIDはまだ存在しないので、後で適用
      setAppliedCoupon(validatedCoupon);
      setShowCouponConfirmation(false);

      // クーポン適用後、直接支払い選択画面へ
      setShowPaymentDialog(true);
      // クーポン適用後の金額を計算してprepareClaimIdに渡す
      const subtotalValue = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const couponDiscountValue = validatedCoupon.discount_type === "fixed" ? validatedCoupon.discount_value : Math.floor(subtotalValue * validatedCoupon.discount_value / 100);
      const manualDiscountValue = discountInfo ? (discountInfo.type === "amount" ? discountInfo.value : Math.floor(subtotalValue * discountInfo.value / 100)) : 0;
      const totalDiscountValue = couponDiscountValue + manualDiscountValue;
      const finalTotal = taxSettings.tax_mode === "internal" || taxSettings.is_tax_exempt ? Math.max(0, subtotalValue - totalDiscountValue) : Math.max(0, Math.floor((subtotalValue - totalDiscountValue) * (1 + (taxSettings.tax_rate || 10) / 100)));
      prepareClaimId(finalTotal);
    } catch (error) {
      console.error('Coupon apply error:', error);
      alert('クーポンの適用に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // クーポン削除
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setValidatedCoupon(null);
  };

  // 値引き適用ハンドラー（高品質版）
  const handleApplyDiscount = () => {
    if (!discountValue || parseInt(discountValue) <= 0) {
      setDiscountError('正しい数値を入力してください');
      return;
    }

    const value = parseInt(discountValue);

    // パーセントの場合は100以下に制限
    if (discountType === 'percent' && value > 100) {
      setDiscountError('値引き率は100％以下で入力してください');
      return;
    }

    // 円値引きの場合は小計以下に制限
    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    if (discountType === 'amount' && value > subtotal) {
      setDiscountError(`値引き額は小計（¥${subtotal.toLocaleString()}）以下で入力してください`);
      return;
    }

    setDiscountInfo({
      type: discountType,
      value: value
    });

    setShowDiscountModal(false);
    setDiscountValue('');
    setDiscountError('');

    // 値引き確定後、直接支払い選択画面へ
    setShowPaymentDialog(true);
    // 値引き後の金額を計算してprepareClaimIdに渡す
    const subtotalValue = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const couponDiscountValue = appliedCoupon ? (appliedCoupon.discount_type === "fixed" ? appliedCoupon.discount_value : Math.floor(subtotalValue * appliedCoupon.discount_value / 100)) : 0;
    const manualDiscountValue = discountType === "amount" ? value : Math.floor(subtotalValue * value / 100);
    const totalDiscountValue = couponDiscountValue + manualDiscountValue;
    const finalTotal = taxSettings.tax_mode === "internal" || taxSettings.is_tax_exempt ? Math.max(0, subtotalValue - totalDiscountValue) : Math.max(0, Math.floor((subtotalValue - totalDiscountValue) * (1 + (taxSettings.tax_rate || 10) / 100)));
    prepareClaimId(finalTotal);
  };

  // 値引き後の金額を計算（プレビュー用）
  const calculateDiscountedTotal = () => {
    if (!discountValue) return getTotal();

    const value = parseInt(discountValue);
    if (isNaN(value)) return getTotal();

    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    let discountAmount = 0;

    if (discountType === 'amount') {
      discountAmount = Math.min(value, subtotal);
    } else if (discountType === 'percent') {
      discountAmount = Math.floor(subtotal * value / 100);
    }

    return Math.max(0, getTotal() - discountAmount);
  };

  // 値引き額を計算（プレビュー用）
  const calculateDiscountAmount = () => {
    if (!discountValue) return 0;

    const value = parseInt(discountValue);
    if (isNaN(value)) return 0;

    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

    if (discountType === 'amount') {
      return Math.min(value, subtotal);
    } else if (discountType === 'percent') {
      return Math.floor(subtotal * value / 100);
    }

    return 0;
  };

  const handlePaymentWithAmount = async (type: 'cash' | 'paypay', amount?: number) => {
    if (orderItems.length === 0) {
      alert('商品を選択してください');
      return;
    }

    // 現金の場合の金額チェック
    if (type === 'cash') {
      if (!amount || amount < getTotal()) {
        alert('お預かり金額が不足しています');
        return;
      }
    }

    setLoading(true);
    try {
      const transactionData = {
        order_type: effectiveOrderType,
        payment_type: type,
        subtotal: getSubtotal(),
        tax: getTax(),
        total: getTotal(),
        tax_mode: taxSettings.tax_mode,
        tax_rate: effectiveOrderType === 'takeout' ? taxSettings.reduced_rate : taxSettings.standard_rate,
        is_tax_exempt: taxSettings.is_tax_exempt,
        items: orderItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        received: type === 'cash' ? amount : undefined,
        change: type === 'cash' && amount ? amount - getTotal() : undefined,
        // 値引き情報を追加
        coupon_discount: appliedCoupon ? getCouponDiscount() : 0,
        manual_discount: discountInfo ? getManualDiscount() : 0,
        discount_amount: (appliedCoupon ? getCouponDiscount() : 0) + (discountInfo ? getManualDiscount() : 0), // レシート表示用（クーポン+手動値引き）
        discount_type: discountInfo ? discountInfo.type : null,
        discount_value: discountInfo ? discountInfo.value : null,
        final_total: getTotal(), // 値引き後の最終金額（getTotal()内で既に値引き済み - 2025-12-28二重値引きバグ修正）
        customer_id: appliedCoupon?.line_user_id
      };

      const response = await apiClient.post('/transactions', transactionData);
      const transactionId = response.data.id;

      const bentoOrderIds = Array.from(new Set(
        orderItems
          .filter(item => item.category === '弁当')
          .map((item) => {
            if (typeof item.bentoOrderId === 'number') return item.bentoOrderId;
            if (!String(item.id).startsWith('BENTO_')) return null;
            const parsedOrderId = Number.parseInt(String(item.id).split('_')[1], 10);
            return Number.isNaN(parsedOrderId) ? null : parsedOrderId;
          })
          .filter((id): id is number => id !== null)
      ));

      // クーポンが適用されている場合は使用済みにする（新バーコード形式対応）
      if (appliedCoupon && appliedCoupon.code) {
        try {
          await apiClient.post('/coupons/use', {
            couponCode: appliedCoupon.code,
            transactionId: transactionId
          });
          console.log('Coupon marked as used:', appliedCoupon.code);
        } catch (couponError) {
          console.error('Failed to mark coupon as used:', couponError);
          // クーポン使用済み処理失敗してもトランザクション自体は成功扱い
        }
      }

      if (response.data.id) {
        if (bentoOrderIds.length > 0) {
          for (const bentoOrderId of bentoOrderIds) {
            try {
              await apiClient.patch(`/bento-orders/${bentoOrderId}/pickup`);
              console.log(`弁当予約 ${bentoOrderId} の受取ステータスを会計後に更新しました`);
            } catch (bentoPickupError) {
              console.warn('会計後の弁当受取ステータス更新に失敗:', bentoPickupError);
            }
          }
        }

        // 仕訳自動生成処理を追加
        try {
          const journalResponse = await apiClient.post('/accounting-export/auto-journal', {
            transactionId: response.data.id
          });

          if (journalResponse.data.success) {
            console.log('仕訳自動生成成功:', journalResponse.data.journalId);
          }
        } catch (journalError) {
          console.error('仕訳生成エラー:', journalError);
          // 仕訳エラーでも販売は続行
        }

        // 在庫減算処理 - 有効化（2026-02-21）
        try {
          const inventoryResponse = await apiClient.post('/inventory-integration/pos-deduct', {
            transactionId: response.data.id,
            dedupe_key: response.data.id,
            items: orderItems.map(item => ({
              menuId: item.id,
              name: item.name,
              quantity: item.quantity
            }))
          });

          if (inventoryResponse.data.deductions) {
            console.log('在庫減算成功:', inventoryResponse.data.deductions);
          }
        } catch (inventoryError) {
          console.error('在庫減算エラー:', inventoryError);
          // 在庫エラーでも販売は続行
        }

        // 実際のtransactionIdでclaim_idを生成
        let finalClaimId = preparedClaimId;
        try {
          const claimResponse = await fetch(`${getApiUrl()}/points/generate-claim`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              transactionId: response.data.id,
              total: getTotal(),
              points: Math.floor(getTotal() / pointsPerYen)
            })
          });

          if (claimResponse.ok) {
            const claimResult = await claimResponse.json();
            if (claimResult.claimId) {
              finalClaimId = claimResult.claimId;
            }
          }
        } catch (error) {
          console.error('Failed to generate final claim ID:', error);
        }

        // レシート用のトランザクションデータを準備
        const receiptTransaction = {
          ...transactionData,
          id: response.data.id,
          timestamp: new Date().toISOString(),
          claimId: finalClaimId,
          points: Math.floor((transactionData.final_total || transactionData.total) / pointsPerYen), // 値引き後の金額でポイント計算（2025-11-17修正）
        };

        console.log('Receipt transaction (handlePaymentWithAmount):', receiptTransaction);

        setLastTransaction(receiptTransaction);
      }

      setShowPaymentDialog(false);
      setShowReceiptPreview(true);
      setOrderItems([]);
      setReceivedAmount('');

      // クーポン・値引き状態をクリア
      setPreparedClaimId(null);
      setPreparedPoints(0);
      setAppliedCoupon(null);
      setValidatedCoupon(null);
      setDiscountInfo(null); // 値引き情報もクリア
      setDiscountValue('');
      setDiscountError('');
    } catch (error) {
      console.error('Payment error:', error);
      alert('決済処理中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (orderItems.length === 0) {
      alert('商品を選択してください');
      return;
    }

    // 現金の場合のみ受取金額をチェック
    if (paymentType === 'cash' && (!receivedAmount || getChange() < 0)) {
      alert('お預かり金額が不足しています');
      return;
    }

    setLoading(true);
    try {
      const transactionData = {
        order_type: effectiveOrderType,
        payment_type: paymentType,
        subtotal: getSubtotal(),
        tax: getTax(),
        total: getTotal(),
        tax_mode: taxSettings.tax_mode,
        tax_rate: effectiveOrderType === 'takeout' ? taxSettings.reduced_rate : taxSettings.standard_rate,
        is_tax_exempt: taxSettings.is_tax_exempt,
        items: orderItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        // 値引き情報を追加
        coupon_discount: appliedCoupon ? getCouponDiscount() : 0,
        manual_discount: discountInfo ? getManualDiscount() : 0,
        discount_amount: (appliedCoupon ? getCouponDiscount() : 0) + (discountInfo ? getManualDiscount() : 0), // レシート表示用（クーポン+手動値引き）
        discount_type: discountInfo ? discountInfo.type : null,
        discount_value: discountInfo ? discountInfo.value : null,
        final_total: getTotal(), // 値引き後の最終金額（getTotal()内で既に値引き済み - 2025-12-28二重値引きバグ修正）
        customer_id: appliedCoupon?.line_user_id
      };

      const response = await apiClient.post('/transactions', transactionData);
      const transactionId = response.data.id;

      const bentoOrderIds = Array.from(new Set(
        orderItems
          .filter(item => item.category === '弁当')
          .map((item) => {
            if (typeof item.bentoOrderId === 'number') return item.bentoOrderId;
            if (!String(item.id).startsWith('BENTO_')) return null;
            const parsedOrderId = Number.parseInt(String(item.id).split('_')[1], 10);
            return Number.isNaN(parsedOrderId) ? null : parsedOrderId;
          })
          .filter((id): id is number => id !== null)
      ));

      // クーポンが適用されている場合は使用済みにする（新バーコード形式対応）
      if (appliedCoupon && appliedCoupon.code) {
        try {
          await apiClient.post('/coupons/use', {
            couponCode: appliedCoupon.code,
            transactionId: transactionId
          });
          console.log('Coupon marked as used:', appliedCoupon.code);
        } catch (couponError) {
          console.error('Failed to mark coupon as used:', couponError);
          // クーポン使用済み処理失敗してもトランザクション自体は成功扱い
        }
      }

      if (response.data.id) {
        if (bentoOrderIds.length > 0) {
          for (const bentoOrderId of bentoOrderIds) {
            try {
              await apiClient.patch(`/bento-orders/${bentoOrderId}/pickup`);
              console.log(`弁当予約 ${bentoOrderId} の受取ステータスを会計後に更新しました`);
            } catch (bentoPickupError) {
              console.warn('会計後の弁当受取ステータス更新に失敗:', bentoPickupError);
            }
          }
        }

        // 仕訳自動生成処理を追加
        try {
          const journalResponse = await apiClient.post('/accounting-export/auto-journal', {
            transactionId: response.data.id
          });

          if (journalResponse.data.success) {
            console.log('仕訳自動生成成功:', journalResponse.data.journalId);
          }
        } catch (journalError) {
          console.error('仕訳生成エラー:', journalError);
          // 仕訳エラーでも販売は続行
        }

        // 在庫減算処理 - 有効化（2026-02-21）
        try {
          const inventoryResponse = await apiClient.post('/inventory-integration/pos-deduct', {
            transactionId: response.data.id,
            dedupe_key: response.data.id,
            items: orderItems.map(item => ({
              menuId: item.id,
              name: item.name,
              quantity: item.quantity
            }))
          });

          if (inventoryResponse.data.deductions) {
            console.log('在庫減算成功:', inventoryResponse.data.deductions);
          }
        } catch (inventoryError) {
          console.error('在庫減算エラー:', inventoryError);
          // 在庫エラーでも販売は続行
        }

        // 実際のtransactionIdでclaim_idを生成
        let finalClaimId = preparedClaimId;
        try {
          const claimResponse = await fetch(`${getApiUrl()}/points/generate-claim`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              transactionId: response.data.id, // 実際のトランザクションID
              total: getTotal(),
              points: Math.floor(getTotal() / pointsPerYen)
            })
          });
          
          if (claimResponse.ok) {
            const claimResult = await claimResponse.json();
            if (claimResult.claimId) {
              finalClaimId = claimResult.claimId;
              console.log('Final claim ID with real transaction:', finalClaimId);
            }
          }
        } catch (error) {
          console.error('Failed to generate final claim ID:', error);
        }
        
        // レシート用のトランザクションデータを準備
        const receiptTransaction = {
          ...transactionData,
          id: response.data.id,
          timestamp: new Date().toISOString(),
          received: paymentType === 'cash' ? parseInt(receivedAmount) : undefined,
          change: paymentType === 'cash' ? getChange() : undefined,
          claimId: finalClaimId, // 実際のトランザクションIDで生成されたclaim_id
          points: Math.floor((transactionData.final_total || transactionData.total) / pointsPerYen), // 値引き後の金額でポイント計算（2025-11-17修正）
          tax_mode: taxSettings.tax_mode,
          tax_rate: effectiveOrderType === 'takeout' ? taxSettings.reduced_rate : taxSettings.standard_rate,
          is_tax_exempt: taxSettings.is_tax_exempt
        };
        console.log('Receipt transaction with claimId:', receiptTransaction);
        console.log('Receipt transaction payment_type:', receiptTransaction.payment_type);
        console.log('Receipt transaction items:', receiptTransaction.items);

        setLastTransaction(receiptTransaction);
        setShowPaymentDialog(false);
        setShowReceiptPreview(true);
        
        // Reset
        setOrderItems([]);
        setReceivedAmount('');
        setOrderType('eatin');
        setPaymentType('cash');
        setPreparedClaimId(null); // claim_idもクリア
        setPreparedPoints(0);
        setAppliedCoupon(null);
        setValidatedCoupon(null);
        setDiscountInfo(null); // 値引き情報もクリア
        setDiscountValue('');
        setDiscountError('');
      }
    } catch (error) {
      console.error('Transaction failed:', error);
      alert('取引に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const quickCashButtons = [1000, 2000, 3000, 5000, 10000];

  // 締め作業を実行
  const performClosingProcess = async () => {
    try {
      const response = await apiClient.post('/daily-closing', {
        closingDate: new Date().toISOString()
      });
      
      if (response.data.success) {
        console.log('締め作業が完了しました:', response.data);
        // 締めレポートを表示して印刷
        setClosingSummary(response.data.summary);
        setShowClosingReport(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('締め作業でエラーが発生しました:', error);
      return false;
    }
  };

  // シャットダウン処理
  const handleShutdown = async (performClosing: boolean) => {
    setLoading(true);
    
    try {
      if (performClosing) {
        // 締め作業を実行（レポート印刷も含む）
        const closingSuccess = await performClosingProcess();
        if (!closingSuccess) {
          alert('締め作業に失敗しました。手動で締め作業を行ってください。');
          setLoading(false);
          return;
        }
        // 印刷完了後の処理はDailyClosingReportのonPrintCompleteで行う
        setLoading(false);
        return;
      } else {
        // 状態を保存（続きから再開用）
        if (orderItems.length > 0) {
          saveSystemState();
        }
        
        // シャットダウンAPIを呼び出し
        await apiClient.post('/shutdown', {
          performClosing,
          closeAll: true
        });
        
        // ブラウザウィンドウを閉じる
        window.close();
        // それでも閉じない場合は、空のページに遷移
        setTimeout(() => {
          window.location.href = 'about:blank';
        }, 100);
      }
      
    } catch (error) {
      console.error('シャットダウン処理でエラー:', error);
      alert('システムの終了に失敗しました。手動で終了してください。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen bg-gray-100">
      {/* 弁当予約通知はApp.tsxでグローバルに表示されるため、ここでは不要 */}

      {/* Header buttons - デスクトップのみ表示 */}
      <div className="hidden lg:flex absolute top-4 left-20 z-40 space-x-3">
        <button
          onClick={() => navigate('/home')}
          className="bg-white text-gray-700 px-4 py-2 rounded-lg shadow hover:bg-gray-100 transition-colors flex items-center space-x-2 border border-gray-300"
        >
          <span className="text-xl">🏠</span>
          <span>ホーム</span>
        </button>
        <button
          onClick={() => {
            setTransactionHistoryFilter('all');
            setShowTransactionHistory(true);
          }}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg shadow hover:bg-purple-700 transition-colors"
        >
          📋 会計履歴
        </button>
        <button
          onClick={() => setShowBentoTimeModal(true)}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-orange-700 flex items-center gap-2"
        >
          <span className="text-xl">⏰</span>
          <span>調理時間</span>
        </button>
        <div className={`px-3 py-2 rounded-lg font-semibold ${
          busyTimeDelay === 0
            ? 'bg-green-100 text-green-800 border border-green-300'
            : busyTimeDelay <= 30
            ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
            : busyTimeDelay <= 60
            ? 'bg-orange-100 text-orange-800 border border-orange-300'
            : 'bg-red-100 text-red-800 border border-red-300'
        }`}>
          {busyTimeDelay === 0 ? '遅延なし' : `+${busyTimeDelay}分遅延`}
        </div>
        <div className="flex items-center gap-4">
          {/* 税制モード表示 */}
          <div className="bg-white px-4 py-2 rounded-lg shadow-lg border-2 border-gray-200">
            {taxSettings.is_tax_exempt ? (
              <span className="text-orange-600 font-bold">🆓 免税事業者</span>
            ) : taxSettings.tax_mode === 'internal' ? (
              <span className="text-blue-600 font-bold">内税表示</span>
            ) : taxSettings.tax_mode === 'external' ? (
              <span className="text-green-600 font-bold">外税表示</span>
            ) : (
              <span className="text-gray-600 font-bold">税設定読み込み中...</span>
            )}
          </div>
          <button
            onClick={() => setShowNotificationSettings(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <span className="text-xl">🔔</span>
            <span>通知設定</span>
          </button>
          <button
            onClick={() => setShowBentoScanModal(true)}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-emerald-700 flex items-center gap-2"
          >
            <span className="text-xl">🧾</span>
            <span>弁当会計</span>
          </button>
          <button
            onClick={() => navigate('/bento-reservation')}
            className={`${
              bentoUnreadCount > 0 ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-blue-600 hover:bg-blue-700'
            } text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 relative transition-all`}
          >
            <span className="text-xl">🍱</span>
            <span>弁当予約管理</span>
            {bentoUnreadCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-yellow-400 text-black rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold border-2 border-white shadow">
                {bentoUnreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowPrizeExchangeModal(true)}
            className="bg-pink-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-pink-700 flex items-center gap-2"
          >
            <span className="text-xl">🎁</span>
            <span>景品交換</span>
          </button>
        </div>
      </div>

      {/* Mobile Header - シンプルに必要最小限 */}
      <div className="lg:hidden absolute top-5 left-16 right-2 z-40 flex items-center justify-between">
        <button
          onClick={() => {
            setTransactionHistoryFilter('takeout');
            setShowTransactionHistory(true);
          }}
          className="bg-orange-600 text-white px-3 py-2 rounded-md shadow hover:bg-orange-700 transition-colors text-xs font-medium"
          title="会計履歴（テイクアウト）"
        >
          会計履歴
        </button>
        <button
          onClick={() => setShowBentoScanModal(true)}
          className="bg-emerald-600 text-white px-3 py-2 rounded-md shadow hover:bg-emerald-700 transition-colors text-xs font-medium"
          title="弁当会計"
        >
          弁当会計
        </button>
        <div className="flex items-center gap-2">
          <div className={`px-2 py-1 rounded text-xs font-bold ${
            busyTimeDelay === 0
              ? 'bg-green-100 text-green-800'
              : busyTimeDelay <= 30
              ? 'bg-yellow-100 text-yellow-800'
              : busyTimeDelay <= 60
              ? 'bg-orange-100 text-orange-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {busyTimeDelay === 0 ? '遅延なし' : `+${busyTimeDelay}分`}
          </div>
          <div className="text-xs font-bold px-2 py-1 bg-blue-100 text-blue-600 rounded">
            {taxSettings.is_tax_exempt ? '免税' : taxSettings.tax_mode === 'internal' ? '内税' : '外税'}
          </div>
        </div>
      </div>

      <div className="flex h-full">
        {/* Left: Menu Items - Takes 70% of screen on desktop, full on mobile */}
        <div className="w-full lg:w-[70%] flex flex-col p-2 lg:p-4">
          {/* Categories */}
          <div className="flex space-x-1 sm:space-x-2 lg:space-x-3 mb-3 lg:mb-5 overflow-x-auto pb-2 mt-16 lg:mt-20 scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-2 sm:px-3 lg:px-5 py-1.5 sm:py-2 lg:py-3 rounded-lg whitespace-nowrap text-xs sm:text-sm lg:text-base font-medium transition-all flex-shrink-0 ${
                  selectedCategory === cat
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white hover:bg-gray-100 shadow'
                }`}
              >
                {cat === 'all' ? '全て' : cat}
              </button>
            ))}
          </div>

          {(selectedCategory ?? "").trim() === "デザート" && (
            <div className="text-xs text-amber-700 mb-2 px-1">
              ※税率は『店内/テイクアウト』の選択に従います
            </div>
          )}

          {/* Menu Grid - Responsive columns */}
          <div className="flex-1 overflow-y-auto px-1 pb-20 lg:pb-4 mt-2.5">
            {loading ? (
              <div className="text-center text-gray-500 mt-12">
                <div className="text-lg mb-4">メニューを読み込み中...</div>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : menus.length === 0 ? (
              <div className="text-center text-gray-500 mt-12">
                <div className="text-lg mb-4">メニューが読み込まれていません</div>
                {errorMessage && (
                  <div className="text-sm mb-4 text-red-600 bg-red-50 p-3 rounded-lg mx-4">
                    {errorMessage}
                  </div>
                )}
                <div className="text-sm mb-4 text-gray-600">
                  <div>現在のURL: {window.location.href}</div>
                  <div>APIエンドポイント: http://{window.location.hostname}:3001</div>
                </div>
                <button 
                  onClick={() => {
                    setErrorMessage('');
                    fetchMenus();
                  }}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:scale-95"
                >
                  メニューを読み込む
                </button>
              </div>
            ) : filteredMenus.length === 0 ? (
              <div className="text-center text-gray-500 mt-12">
                <div className="text-lg">該当するメニューがありません</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-3">
                {filteredMenus.map(menu => {
                  const serviceType = menu.service_type || (menu.is_takeout_available === false ? 'eatin_only' : 'both');
                  const isRestricted =
                    (orderType === 'takeout' && serviceType === 'eatin_only') ||
                    (orderType === 'eatin' && serviceType === 'takeout_only');
                  return (
                    <button
                      key={menu.id}
                      onClick={() => addToOrder(menu)}
                      className={`relative p-2 sm:p-3 lg:p-4 rounded-lg shadow transition-all ${
                        isRestricted
                          ? 'bg-gray-100 cursor-not-allowed opacity-60'
                          : 'bg-white hover:shadow-lg hover:scale-105 active:scale-95'
                      }`}
                      disabled={isRestricted}
                    >
                      {isRestricted && (
                        <div className="absolute top-1 right-1 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {orderType === 'takeout' ? '店内専用' : 'テイク専用'}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mb-1">{menu.category}</div>
                      <div className={`font-bold text-xs sm:text-sm lg:text-base mb-1 sm:mb-2 leading-tight min-h-[1.5rem] sm:min-h-[2rem] lg:min-h-[2.5rem] ${
                        isRestricted ? 'text-gray-500' : 'text-gray-800'
                      }`}>
                        {menu.name}
                      </div>
                      <div className={`text-base sm:text-lg lg:text-xl font-bold ${
                        isRestricted ? 'text-gray-400' : 'text-blue-600'
                      }`}>
                        ¥{menu.price.toLocaleString()}
                      </div>
                      {/* 在庫表示 - 非表示（2026-02-03）
                      {menu.stock > 0 && !isRestricted && (
                        <div className="text-xs text-gray-400 mt-1 hidden sm:block">在庫: {menu.stock}</div>
                      )}
                      */}
                      {serviceType === 'eatin_only' && (
                        <div className="text-xs text-orange-600 mt-1 font-medium">
                          🍽️ 店内専用
                        </div>
                      )}
                      {serviceType === 'takeout_only' && (
                        <div className="text-xs text-blue-600 mt-1 font-medium">
                          🥡 テイクアウト専用
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: Order Summary - Hidden on mobile, 30% on desktop */}
        <div className="hidden lg:flex w-[30%] bg-white shadow-2xl flex-col">
          <div className="p-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <h2 className="text-3xl font-bold text-center">注文内容</h2>
          </div>

          {/* Order Type */}
          <div className="p-4 border-b">
            <div className="flex space-x-2">
              <button
                onClick={() => !isEventMode && setOrderType('eatin')}
                disabled={isEventMode}
                className={`flex-1 py-2.5 rounded-lg text-base font-medium transition-all ${
                  orderType === 'eatin'
                    ? 'bg-blue-600 text-white'
                    : isEventMode
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                🍽️ 店内
              </button>
              <button
                onClick={() => setOrderType('takeout')}
                className={`flex-1 py-2.5 rounded-lg text-base font-medium transition-all ${
                  orderType === 'takeout'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                🥡 テイクアウト
              </button>
            </div>
            {isEventMode && (
              <div className="mt-2 text-xs text-orange-600 bg-orange-50 p-2 rounded">
                🚚 出張モード: テイクアウト専用
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {orderItems.length === 0 ? (
              <div className="text-center text-gray-500 mt-12">
                <div className="text-5xl mb-3">🛒</div>
                <div className="text-lg">商品を選択してください</div>
              </div>
            ) : (
              <div className="space-y-3">
                {orderItems.map(item => (
                  <div key={item.id} className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 mr-4">
                        <div className="font-bold text-base text-gray-800">{item.name}</div>
                        <div className="text-gray-600 text-sm mt-1">¥{item.price.toLocaleString()}</div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 bg-red-500 text-white rounded-full text-lg font-bold hover:bg-red-600 active:scale-95"
                        >
                          −
                        </button>
                        <span className="w-10 text-center font-bold text-lg">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 bg-blue-500 text-white rounded-full text-lg font-bold hover:bg-blue-600 active:scale-95"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="text-right mt-2 text-base font-bold text-gray-700">
                      小計: ¥{(item.price * item.quantity).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="p-4 border-t bg-gray-50">
            <div className="space-y-2">
              <div className="flex justify-between text-base">
                <span className="text-gray-600">小計</span>
                <span className="font-bold">¥{getSubtotal().toLocaleString()}</span>
              </div>
              {getTax() > 0 && (
                <div className="flex justify-between text-base">
                  <span className="text-gray-600">
                    {taxSettings.tax_mode === 'internal' ?
                      `（内消費税 ${effectiveOrderType === 'takeout' ? taxSettings.reduced_rate : taxSettings.standard_rate}%）` :
                     taxSettings.tax_mode === 'external' ?
                      `消費税（${effectiveOrderType === 'takeout' ? taxSettings.reduced_rate : taxSettings.standard_rate}%）` : ''}
                  </span>
                  <span className="font-bold">¥{getTax().toLocaleString()}</span>
                </div>
              )}
              {taxSettings.is_tax_exempt && (
                <div className="flex justify-between text-base text-orange-600">
                  <span>【免税事業者モード】</span>
                  <span className="font-bold">非課税</span>
                </div>
              )}
              {appliedCoupon && (
                <div className="flex justify-between text-base text-red-600">
                  <span className="flex items-center">
                    🎫 クーポン割引
                    <button
                      onClick={handleRemoveCoupon}
                      className="ml-2 text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded"
                    >
                      削除
                    </button>
                  </span>
                  <span className="font-bold">-¥{getCouponDiscount().toLocaleString()}</span>
                </div>
              )}
              {discountInfo && (
                <div className="flex justify-between text-base text-orange-600">
                  <span className="flex items-center">
                    💰 値引き ({discountInfo.type === 'percent' ? `${discountInfo.value}%` : `¥${discountInfo.value}`})
                    <button
                      onClick={() => setDiscountInfo(null)}
                      className="ml-2 text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 px-2 py-1 rounded"
                    >
                      削除
                    </button>
                  </span>
                  <span className="font-bold">-¥{getManualDiscount().toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold pt-2 border-t">
                <span>合計</span>
                <span className="text-blue-600">¥{getTotal().toLocaleString()}</span>
              </div>
            </div>

            {/* クーポン・値引きボタン（2列グリッド、常に表示、相互排他） */}
            {orderItems.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <button
                  onClick={() => setShowCouponModal(true)}
                  disabled={!!appliedCoupon || !!discountInfo}
                  className={`py-3 rounded-lg font-bold text-lg transition-all ${
                    appliedCoupon || discountInfo
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                  }`}
                >
                  🎫 クーポン使用
                </button>
                <button
                  onClick={() => setShowDiscountModal(true)}
                  disabled={!!discountInfo || !!appliedCoupon}
                  className={`py-3 rounded-lg font-bold text-lg transition-all ${
                    discountInfo || appliedCoupon
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                  }`}
                >
                  💰 値引き
                </button>
              </div>
            )}

            <button
              onClick={() => {
                console.log('Opening payment dialog, preparing claim ID...');
                setShowPaymentDialog(true);
                // claim_idを事前生成
                prepareClaimId();
              }}
              disabled={orderItems.length === 0}
              className="w-full mt-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold text-lg disabled:from-gray-400 disabled:to-gray-400 hover:shadow-lg active:scale-95 transition-all"
            >
              会計へ進む
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Cart Button - 大きくして見やすく */}
      <div className="lg:hidden fixed bottom-6 right-6 z-40">
        <button
          onClick={() => setShowMobileCart(true)}
          className="bg-blue-600 text-white p-6 rounded-full shadow-2xl relative transform hover:scale-110 transition-transform"
        >
          <span className="text-4xl">🛒</span>
          {orderItems.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold">
              {orderItems.reduce((sum, item) => sum + item.quantity, 0)}
            </span>
          )}
        </button>
      </div>

      {/* Mobile Cart Modal */}
      {showMobileCart && (
        <div className="lg:hidden fixed inset-0 bg-white z-50 flex flex-col">
          <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex justify-between items-center">
            <h2 className="text-2xl font-bold">注文内容</h2>
            <button
              onClick={() => setShowMobileCart(false)}
              className="text-white text-2xl"
            >
              ✕
            </button>
          </div>

          {/* Order Type */}
          <div className="p-4 border-b">
            <div className="flex space-x-2">
              <button
                onClick={() => !isEventMode && setOrderType('eatin')}
                disabled={isEventMode}
                className={`flex-1 py-3 rounded-lg text-base font-medium ${
                  orderType === 'eatin'
                    ? 'bg-blue-600 text-white'
                    : isEventMode
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200'
                }`}
              >
                🍽️ 店内
              </button>
              <button
                onClick={() => setOrderType('takeout')}
                className={`flex-1 py-3 rounded-lg text-base font-medium ${
                  orderType === 'takeout'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200'
                }`}
              >
                🥡 テイクアウト
              </button>
            </div>
            {isEventMode && (
              <div className="mt-2 text-xs text-orange-600 bg-orange-50 p-2 rounded">
                🚚 出張モード: テイクアウト専用
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {orderItems.length === 0 ? (
              <div className="text-center text-gray-500 mt-12">
                <div className="text-5xl mb-3">🛒</div>
                <div className="text-lg">商品を選択してください</div>
              </div>
            ) : (
              <div className="space-y-3">
                {orderItems.map(item => (
                  <div key={item.id} className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-bold text-base">{item.name}</div>
                        <div className="text-gray-600 text-sm">¥{item.price.toLocaleString()}</div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 bg-red-500 text-white rounded-full"
                        >
                          −
                        </button>
                        <span className="w-8 text-center font-bold">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 bg-blue-500 text-white rounded-full"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="text-right mt-2 font-bold">
                      小計: ¥{(item.price * item.quantity).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="p-4 border-t bg-gray-50">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>小計</span>
                <span className="font-bold">¥{getSubtotal().toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>（内消費税）</span>
                <span className="font-bold">¥{getTax().toLocaleString()}</span>
              </div>
              {getCouponDiscount() > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>クーポン割引</span>
                  <span className="font-bold">-¥{getCouponDiscount().toLocaleString()}</span>
                </div>
              )}
              {getManualDiscount() > 0 && (
                <div className="flex justify-between text-orange-600">
                  <span>値引き</span>
                  <span className="font-bold">-¥{getManualDiscount().toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold pt-2 border-t">
                <span>合計</span>
                <span className="text-blue-600">¥{getTotal().toLocaleString()}</span>
              </div>
            </div>

            {/* クーポン・値引きボタン（モバイル、2列グリッド、相互排他） */}
            {orderItems.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <button
                  onClick={() => {
                    setShowMobileCart(false);
                    setShowCouponModal(true);
                  }}
                  disabled={!!appliedCoupon || !!discountInfo}
                  className={`py-3 rounded-lg font-bold text-lg ${
                    appliedCoupon || discountInfo
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-purple-600 text-white'
                  }`}
                >
                  🎫 クーポン使用
                </button>
                <button
                  onClick={() => {
                    setShowMobileCart(false);
                    setShowDiscountModal(true);
                  }}
                  disabled={!!discountInfo || !!appliedCoupon}
                  className={`py-3 rounded-lg font-bold text-lg ${
                    discountInfo || appliedCoupon
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-orange-600 text-white'
                  }`}
                >
                  💰 値引き
                </button>
              </div>
            )}

            <button
              onClick={() => {
                setShowMobileCart(false);
                setShowPaymentDialog(true);
              }}
              disabled={orderItems.length === 0}
              className="w-full mt-3 py-3 bg-green-600 text-white rounded-lg font-bold text-lg disabled:bg-gray-400"
            >
              会計へ進む
            </button>
          </div>
        </div>
      )}

      {/* Payment Calculator Enhanced */}
      {showPaymentDialog && (
        <PaymentCalculatorEnhanced
          total={getTotal()}
          onClose={() => {
            setShowPaymentDialog(false);
            setPreparedClaimId(null);
          }}
          onBackToOrder={() => {
            setShowPaymentDialog(false);
            setPreparedClaimId(null);
          }}
          onPaymentComplete={async (type, amount) => {
            setPaymentType(type);
            if (type === 'cash' && amount) {
              setReceivedAmount(amount.toString());
              // 直接amountを使用してhandlePaymentWithAmountを呼び出す
              await handlePaymentWithAmount(type, amount);
            } else if (type === 'paypay') {
              // PayPayの場合はhandlePaymentWithAmountを使用（receivedAmountチェックをスキップ）
              await handlePaymentWithAmount(type);
            } else {
              await handlePayment();
            }
          }}
        />
      )}

      {/* Old Payment Dialog (Disabled) */}
      {false && showPaymentDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg">
            {/* 決済方法のヘッダーを削除 */}
            
            <div className="mb-6">
              <div className="text-blue-600 mb-6 py-2 flex items-baseline">
                <span className="text-2xl font-bold">　お会計金額　</span>
                <span className="text-4xl font-bold">¥{getTotal().toLocaleString()}</span>
              </div>

              {/* Payment Type */}
              <div className="flex space-x-3 mb-6">
                <button
                  onClick={() => setPaymentType('cash')}
                  className={`flex-1 py-3 rounded-lg text-lg font-medium transition-all ${
                    paymentType === 'cash'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  💵 現金
                </button>
                <button
                  onClick={() => setPaymentType('paypay')}
                  className={`flex-1 py-3 rounded-lg text-lg font-medium transition-all ${
                    paymentType === 'paypay'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  📱 PayPay
                </button>
              </div>

              {/* Cash Payment */}
              {paymentType === 'cash' && (
                <div>
                  {/* 受取金額のラベルを削除 */}
                  <input
                    type="text"
                    value={receivedAmount}
                    onChange={(e) => {
                      // 数字のみ許可
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setReceivedAmount(value);
                    }}
                    className="w-full px-4 py-3 border-2 rounded-lg text-2xl mb-4 text-right font-mono"
                    placeholder="0"
                    readOnly
                  />
                  
                  {/* テンキー */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {/* 数字ボタン 7-9 */}
                    {[7, 8, 9].map(num => (
                      <button
                        key={num}
                        onClick={() => setReceivedAmount(receivedAmount + num)}
                        className="py-4 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 text-xl font-bold"
                      >
                        {num}
                      </button>
                    ))}
                    <button
                      onClick={() => setReceivedAmount('')}
                      className="py-4 bg-red-500 text-white rounded-lg hover:bg-red-600 text-lg font-bold"
                    >
                      AC
                    </button>
                    
                    {/* 数字ボタン 4-6 */}
                    {[4, 5, 6].map(num => (
                      <button
                        key={num}
                        onClick={() => setReceivedAmount(receivedAmount + num)}
                        className="py-4 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 text-xl font-bold"
                      >
                        {num}
                      </button>
                    ))}
                    <button
                      onClick={() => setReceivedAmount(receivedAmount.slice(0, -1))}
                      className="py-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-lg font-bold"
                    >
                      C
                    </button>
                    
                    {/* 数字ボタン 1-3 */}
                    {[1, 2, 3].map(num => (
                      <button
                        key={num}
                        onClick={() => setReceivedAmount(receivedAmount + num)}
                        className="py-4 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 text-xl font-bold"
                      >
                        {num}
                      </button>
                    ))}
                    <button
                      onClick={() => setReceivedAmount(receivedAmount + '00')}
                      className="py-4 bg-gray-200 rounded-lg hover:bg-gray-300 text-lg font-bold"
                    >
                      00
                    </button>
                    
                    {/* 0と000 */}
                    <button
                      onClick={() => setReceivedAmount(receivedAmount + '0')}
                      className="py-4 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 text-xl font-bold col-span-2"
                    >
                      0
                    </button>
                    <button
                      onClick={() => setReceivedAmount(receivedAmount + '000')}
                      className="py-4 bg-gray-200 rounded-lg hover:bg-gray-300 text-lg font-bold col-span-2"
                    >
                      000
                    </button>
                  </div>
                  
                  {/* Quick Cash Buttons (積算式) */}
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {quickCashButtons.map(amount => (
                      <button
                        key={amount}
                        onClick={() => {
                          const currentAmount = parseInt(receivedAmount) || 0;
                          setReceivedAmount((currentAmount + amount).toString());
                        }}
                        className="py-3 bg-green-100 border-2 border-green-300 rounded-lg hover:bg-green-200 text-md font-medium"
                      >
                        +¥{amount.toLocaleString()}
                      </button>
                    ))}
                    <button
                      onClick={() => setReceivedAmount(getTotal().toString())}
                      className="py-3 bg-blue-200 rounded-lg hover:bg-blue-300 text-lg font-medium"
                    >
                      ちょうど
                    </button>
                  </div>

                  {receivedAmount && (
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className={`text-3xl font-bold ${getChange() >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        お釣り　¥{getChange().toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* PayPay Payment */}
              {paymentType === 'paypay' && (
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <div className="text-6xl mb-4">📱</div>
                  <div className="text-xl font-bold mb-2">PayPay決済</div>
                  <div className="text-lg text-gray-600">
                    お客様のPayPayアプリで
                    <br />支払いを完了してください
                  </div>
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowPaymentDialog(false)}
                className="flex-1 py-3 bg-gray-300 rounded-lg text-lg font-medium hover:bg-gray-400"
                disabled={loading}
              >
                キャンセル
              </button>
              <button
                onClick={handlePayment}
                disabled={loading || (paymentType === 'cash' && getChange() < 0)}
                className="flex-1 py-3 bg-green-600 text-white rounded-lg text-lg font-medium disabled:bg-gray-400 hover:bg-green-700"
              >
                {loading ? '処理中...' : '決済完了'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* レシートプレビュー */}
      {showReceiptPreview && lastTransaction && (
        <ReceiptPreview
          transaction={lastTransaction}
          isOpen={showReceiptPreview}
          onPrint={async () => {
            try {
              await printReceiptHTML(lastTransaction);
              setShowReceiptPreview(false);
            } catch (error) {
              console.error('Print error:', error);
              alert(`印刷エラー: ${error}`);
            }
          }}
          onSavePDF={async () => {
            console.log('Starting PDF generation...');
            try {
              await generatePDF(lastTransaction);
              alert('PDFが保存されました');
            } catch (error) {
              console.error('PDF generation error:', error);
              alert(`PDF生成エラー: ${error}`);
            }
          }}
          onClose={() => setShowReceiptPreview(false)}
        />
      )}

      {/* Transaction History Modal */}
      <TransactionHistory
        isOpen={showTransactionHistory}
        onClose={() => setShowTransactionHistory(false)}
        initialFilter={transactionHistoryFilter}
      />

      {/* Bento Time Adjustment Modal */}
      {showBentoTimeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-4 w-full max-w-md relative">
            <button
              onClick={() => setShowBentoTimeModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
            >
              ✕
            </button>
            <BentoTimeAdjuster />
          </div>
        </div>
      )}

      {/* 締めレポート印刷 */}
      {showClosingReport && closingSummary && (
        <DailyClosingReportHTML 
          summary={closingSummary}
          onPrintComplete={() => {
            setShowClosingReport(false);
            // 締め作業後の処理を継続
            clearSystemState();
            // システム終了処理
            setTimeout(() => {
              window.close();
              setTimeout(() => {
                window.location.href = 'about:blank';
              }, 100);
            }, 1000);
          }}
        />
      )}

      {/* 終了確認ダイアログ */}
      {showExitDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md">
            <h3 className="text-2xl font-bold mb-6">システムを終了しますか？</h3>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                本日の営業は終了ですか？締め作業を行いますか？
              </p>
              
              {orderItems.length > 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-lg mb-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ 現在、未会計の注文が {orderItems.length} 件あります
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowExitDialog(false);
                  setShowClosingConfirm(true);
                }}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                締め作業を行って終了
              </button>
              
              <button
                onClick={() => {
                  setShowExitDialog(false);
                  handleShutdown(false);
                }}
                className="w-full py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700"
              >
                締め作業をせずに終了（再開可能）
              </button>
              
              <button
                onClick={() => setShowExitDialog(false)}
                className="w-full py-3 bg-gray-300 rounded-lg font-medium hover:bg-gray-400"
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 締め作業確認ダイアログ */}
      {showClosingConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md">
            <h3 className="text-2xl font-bold mb-6 text-red-600">締め作業の最終確認</h3>
            
            <div className="mb-6">
              <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
                <p className="font-bold text-red-800 mb-2">
                  ⚠️ 締め作業を実行すると：
                </p>
                <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                  <li>本日の売上が確定されます</li>
                  <li>日次レポートが生成されます</li>
                  <li>システムが完全にリセットされます</li>
                  <li>この操作は取り消せません</li>
                </ul>
              </div>
              
              {orderItems.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    未会計の注文は破棄されます
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowClosingConfirm(false);
                  handleShutdown(true);
                }}
                className="w-full py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700"
                disabled={loading}
              >
                {loading ? '処理中...' : '締め作業を実行して終了'}
              </button>
              
              <button
                onClick={() => setShowClosingConfirm(false)}
                className="w-full py-3 bg-gray-300 rounded-lg font-medium hover:bg-gray-400"
                disabled={loading}
              >
                戻る
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 通知設定ドロワー */}
      <Drawer
        title="弁当予約通知設定"
        placement="right"
        onClose={() => setShowNotificationSettings(false)}
        open={showNotificationSettings}
        width={400}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* サウンド設定 */}
          <Card title="サウンド設定" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>サウンド通知</span>
                <Switch
                  defaultChecked={localStorage.getItem('bentoNotificationSettings') ?
                    JSON.parse(localStorage.getItem('bentoNotificationSettings') || '{}').soundEnabled : true}
                  onChange={(checked) => {
                    const settings = JSON.parse(localStorage.getItem('bentoNotificationSettings') || '{}');
                    settings.soundEnabled = checked;
                    localStorage.setItem('bentoNotificationSettings', JSON.stringify(settings));
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>サウンドファイル</span>
                <Select
                  style={{ width: 150 }}
                  defaultValue="/sounds/chime1.mp3"
                  onChange={(value) => {
                    const settings = JSON.parse(localStorage.getItem('bentoNotificationSettings') || '{}');
                    settings.soundFile = value;
                    localStorage.setItem('bentoNotificationSettings', JSON.stringify(settings));
                  }}
                >
                  <Select.Option value="/sounds/chime1.mp3">チャイム1</Select.Option>
                  <Select.Option value="/sounds/chime2.mp3">チャイム2</Select.Option>
                  <Select.Option value="/sounds/bell1.mp3">ベル1</Select.Option>
                  <Select.Option value="/sounds/bell2.mp3">ベル2</Select.Option>
                  <Select.Option value="/sounds/notification.mp3">通知音</Select.Option>
                </Select>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>音量</span>
                <InputNumber
                  min={0}
                  max={1}
                  step={0.1}
                  defaultValue={0.7}
                  formatter={value => `${(value as number * 100).toFixed(0)}%`}
                  onChange={(value) => {
                    const settings = JSON.parse(localStorage.getItem('bentoNotificationSettings') || '{}');
                    settings.soundVolume = value;
                    localStorage.setItem('bentoNotificationSettings', JSON.stringify(settings));
                  }}
                />
              </div>
            </Space>
          </Card>

          {/* リマインダー設定 */}
          <Card title="リマインダー設定" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>リマインダー</span>
                <Switch
                  defaultChecked={localStorage.getItem('bentoNotificationSettings') ?
                    JSON.parse(localStorage.getItem('bentoNotificationSettings') || '{}').reminderEnabled : true}
                  onChange={(checked) => {
                    const settings = JSON.parse(localStorage.getItem('bentoNotificationSettings') || '{}');
                    settings.reminderEnabled = checked;
                    localStorage.setItem('bentoNotificationSettings', JSON.stringify(settings));
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>リマインダー間隔</span>
                <Select
                  style={{ width: 120 }}
                  defaultValue={2}
                  onChange={(value) => {
                    const settings = JSON.parse(localStorage.getItem('bentoNotificationSettings') || '{}');
                    settings.reminderInterval = value;
                    localStorage.setItem('bentoNotificationSettings', JSON.stringify(settings));
                  }}
                >
                  <Select.Option value={1}>1分</Select.Option>
                  <Select.Option value={2}>2分</Select.Option>
                  <Select.Option value={3}>3分</Select.Option>
                  <Select.Option value={5}>5分</Select.Option>
                  <Select.Option value={10}>10分</Select.Option>
                </Select>
              </div>
            </Space>
          </Card>

          {/* 通知チェック設定 */}
          <Card title="通知チェック設定" size="small">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>チェック間隔</span>
              <Select
                style={{ width: 120 }}
                defaultValue={10}
                onChange={(value) => {
                  const settings = JSON.parse(localStorage.getItem('bentoNotificationSettings') || '{}');
                  settings.checkInterval = value;
                  localStorage.setItem('bentoNotificationSettings', JSON.stringify(settings));
                }}
              >
                <Select.Option value={5}>5秒</Select.Option>
                <Select.Option value={10}>10秒</Select.Option>
                <Select.Option value={30}>30秒</Select.Option>
                <Select.Option value={60}>60秒</Select.Option>
              </Select>
            </div>
          </Card>

          <div style={{ padding: '12px', background: '#f0f0f0', borderRadius: '8px', fontSize: '12px', color: '#666' }}>
            💡 設定は自動的に保存され、次回起動時にも適用されます
          </div>
        </Space>
      </Drawer>
      {/* クーポンPIN入力モーダル */}
      <BentoScanModal
        isOpen={showBentoScanModal}
        onClose={() => setShowBentoScanModal(false)}
        onScan={handleBentoScan}
      />

      <CouponInputModal
        isOpen={showCouponModal}
        onClose={() => setShowCouponModal(false)}
        onCouponValidated={handleCouponValidated}
        subtotal={getSubtotal()}
      />

      {/* クーポン確認画面 */}
      {showCouponConfirmation && validatedCoupon && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">クーポン確認</h2>

            {/* クーポン詳細 */}
            <div className="space-y-4 mb-6">
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">クーポン名</div>
                <div className="text-xl font-bold text-gray-800">{validatedCoupon.title}</div>
              </div>

              {validatedCoupon.description && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">説明</div>
                  <div className="text-gray-800">{validatedCoupon.description}</div>
                </div>
              )}

              {validatedCoupon.member_name && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">会員名</div>
                  <div className="text-lg font-bold text-gray-800">{validatedCoupon.member_name}</div>
                  {validatedCoupon.points !== undefined && (
                    <div className="text-sm text-gray-600 mt-1">
                      現在のポイント: {validatedCoupon.points}pt
                    </div>
                  )}
                </div>
              )}

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">割引内容</div>
                <div className="text-2xl font-bold text-green-600">
                  {validatedCoupon.discount_type === 'fixed' && (
                    <>¥{validatedCoupon.discount_value.toLocaleString()} 割引</>
                  )}
                  {validatedCoupon.discount_type === 'percentage' && (
                    <>{validatedCoupon.discount_value}% 割引</>
                  )}
                  {validatedCoupon.discount_type === 'item' && (
                    <>商品特典</>
                  )}
                </div>
              </div>

              {validatedCoupon.usage_limit > 1 && (
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">使用回数</div>
                  <div className="text-lg font-bold text-yellow-700">
                    {validatedCoupon.times_used || 0} / {validatedCoupon.usage_limit}回使用済み
                    <span className="text-sm font-normal ml-2">
                      （残り{validatedCoupon.remaining}回）
                    </span>
                  </div>
                </div>
              )}

              {validatedCoupon.minimum_purchase && (
                <div className="text-sm text-gray-600">
                  ※最低購入金額: ¥{validatedCoupon.minimum_purchase.toLocaleString()}
                </div>
              )}

              <div className="text-sm text-gray-500">
                有効期限: {new Date(validatedCoupon.expire_date).toLocaleDateString('ja-JP')}
              </div>
            </div>

            {/* アクションボタン */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCouponConfirmation(false);
                  setValidatedCoupon(null);
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 rounded-lg transition"
              >
                キャンセル
              </button>
              <button
                onClick={handleApplyCoupon}
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition disabled:opacity-50"
              >
                {loading ? '適用中...' : '適用する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 高品質値引きモーダル */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
            {/* ヘッダー */}
            <h2 className="text-2xl font-bold mb-6 text-gray-800">値引き設定</h2>

            {/* タブ切り替え */}
            <div className="flex space-x-3 mb-6">
              <button
                onClick={() => {
                  setDiscountType('amount');
                  setDiscountValue('');
                  setDiscountError('');
                }}
                className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                  discountType === 'amount'
                    ? 'bg-orange-600 text-white shadow-lg'
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                💴 円値引き
              </button>
              <button
                onClick={() => {
                  setDiscountType('percent');
                  setDiscountValue('');
                  setDiscountError('');
                }}
                className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                  discountType === 'percent'
                    ? 'bg-orange-600 text-white shadow-lg'
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                📊 ％値引き
              </button>
            </div>

            {/* 入力フィールド */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {discountType === 'amount' ? '値引き額（円）' : '値引き率（％）'}
              </label>
              <input
                type="text"
                inputMode="none"
                value={discountValue}
                onChange={(e) => {
                  setDiscountValue(e.target.value);
                  setDiscountError('');
                }}
                className="w-full px-4 py-3 border-2 rounded-lg text-2xl text-right font-mono focus:border-orange-500 focus:outline-none"
                placeholder={discountType === 'amount' ? '100' : '10'}
                readOnly
              />

              {/* テンキーパッド */}
              <div className="grid grid-cols-3 gap-2 mt-3">
                {[1,2,3,4,5,6,7,8,9,'C',0,'←'].map(key => (
                  <button
                    key={key}
                    onClick={() => {
                      if (key === 'C') {
                        setDiscountValue('');
                        setDiscountError('');
                      } else if (key === '←') {
                        setDiscountValue(discountValue.slice(0, -1));
                      } else {
                        setDiscountValue(discountValue + key.toString());
                      }
                    }}
                    className="py-4 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold text-lg transition-all active:scale-95"
                  >
                    {key}
                  </button>
                ))}
              </div>

              {/* リアルタイムプレビュー */}
              {discountValue && !discountError && (
                <div className="mt-3 p-3 bg-orange-50 rounded-lg">
                  <div className="text-sm text-gray-600">値引き後の金額</div>
                  <div className="text-2xl font-bold text-orange-600">
                    ¥{calculateDiscountedTotal().toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500">
                    （-¥{calculateDiscountAmount().toLocaleString()}）
                  </div>
                </div>
              )}
            </div>

            {/* エラー表示 */}
            {discountError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">
                ⚠️ {discountError}
              </div>
            )}

            {/* アクションボタン */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDiscountModal(false);
                  setDiscountValue('');
                  setDiscountError('');
                }}
                className="flex-1 py-3 bg-gray-300 rounded-lg font-medium hover:bg-gray-400 transition-all"
              >
                キャンセル
              </button>
              <button
                onClick={handleApplyDiscount}
                disabled={!discountValue || parseInt(discountValue) <= 0}
                className="flex-1 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:bg-gray-400 transition-all"
              >
                適用する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 景品交換モーダル */}
      {showPrizeExchangeModal && (
        <PrizeExchangeModal
          isOpen={showPrizeExchangeModal}
          onClose={() => setShowPrizeExchangeModal(false)}
          onApply={(prizeValue) => {
            // 景品交換時の処理（必要に応じて実装）
            console.log('Prize exchange:', prizeValue);
            setShowPrizeExchangeModal(false);
          }}
        />
      )}
    </div>
  );
};

export default POSRegisterFullScreen;
